import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import Snackbar from '@sorare/core/src/atoms/snackbar/Snackbar';
import { BodyM, HeadlineL } from '@sorare/core/src/atoms/typography';
import { FOOTBALL_PLAY_RIVALS_LEAGUES_INVITE_JOINSECRET_USERSLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import useCopyToClipboard from '@sorare/core/src/hooks/useCopyToClipboard';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { ShareBlock_rivalsUserGroup } from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  min-width: 300px;
  max-width: 700px;
  @media ${tabletAndAbove} {
    min-width: 400px;
  }
`;

const Title = styled(Horizontal)`
  justify-content: space-between;
`;

const SecretBlockItem = styled(Vertical).attrs({ center: true })`
  background: var(--c-neutral-150);
  border-radius: var(--half-unit);
  aspect-ratio: 1;
`;

const SecretBlock = styled(Horizontal)`
  & > * {
    flex: 1;
  }
`;

type Props = {
  userGroup: Nullable<ShareBlock_rivalsUserGroup>;
};

export const ShareBlock = ({ userGroup }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const [copiedSnackbarOpen, setCopiedSnackbarOpen] = useState(false);
  const copyToClipboard = useCopyToClipboard();
  const { formatMessage } = useIntl();
  const onCopyLink = () => {
    copyToClipboard(
      `${formatMessage({
        id: 'ShareBlock.inviteMessage2',
        defaultMessage:
          'Join my co-op Sorare fantasy league! Everyone wins something, and the top prize is playing 5-a-side football with Zidane! #EuroFantasy',
      })} ${window.origin}${generatePath(
        FOOTBALL_PLAY_RIVALS_LEAGUES_INVITE_JOINSECRET_USERSLUG,
        {
          joinSecret: userGroup!.joinSecret,
          userSlug: currentUser!.slug,
        }
      )}`
    );
    setCopiedSnackbarOpen(true);
  };
  const onCopyCode = () => {
    copyToClipboard(userGroup!.joinSecret);
    setCopiedSnackbarOpen(true);
  };

  return (
    <Wrapper gap={4}>
      <Vertical gap={1}>
        <Title>
          <BodyM bold>
            <FormattedMessage
              id="ShareBlock.title"
              defaultMessage="Your League Code"
            />
          </BodyM>
          <ButtonBase onClick={onCopyCode}>
            <BodyM bold color="var(--c-brand-600)">
              <FormattedMessage {...glossary.copy} />
            </BodyM>
          </ButtonBase>
        </Title>
        <SecretBlock
          disabled={!userGroup?.joinSecret}
          as={ButtonBase}
          onClick={onCopyCode}
        >
          {Array.from(userGroup?.joinSecret || '').map((c, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <SecretBlockItem key={index}>
              <HeadlineL>{c}</HeadlineL>
            </SecretBlockItem>
          ))}
        </SecretBlock>
      </Vertical>
      <Button
        size="large"
        color="secondary"
        onClick={onCopyLink}
        disabled={!userGroup?.joinSecret}
      >
        <FormattedMessage
          id="ShareBlock.inviteFriends"
          defaultMessage="Invite Friends"
        />
      </Button>
      <Snackbar
        autoHideDuration={2000}
        open={copiedSnackbarOpen}
        onClose={() => {
          setCopiedSnackbarOpen(false);
        }}
        message={formatMessage(glossary.copiedToClipboard)}
      />
    </Wrapper>
  );
};

ShareBlock.fragments = {
  rivalsUserGroup: gql`
    fragment ShareBlock_rivalsUserGroup on RivalsUserGroup {
      slug
      joinSecret
    }
  ` as TypedDocumentNode<ShareBlock_rivalsUserGroup>,
};
